import { useEffect, useState } from "react";
import AdminShell from "@/components/admin/AdminShell";
import api, { formatApiError } from "@/lib/api";
import VideoEmbed from "@/components/app/VideoEmbed";
import { Save, RotateCcw, Video, FileText, Users, GraduationCap, BookOpen, Eye, ChevronDown, ChevronRight, Check, ClipboardList, Award, Shield, Upload } from "lucide-react";
import { Link } from "react-router-dom";

/** One-tap "seed" import: pulls every video/text override captured in preview and applies
 *  them here so a fresh production DB doesn't require re-pasting 100+ embeds. Safe by default —
 *  existing overrides in this environment are NOT replaced unless "Overwrite existing" is checked.
 */
function ImportSeed({ onDone }) {
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [overwrite, setOverwrite] = useState(false);
  const [err, setErr] = useState("");
  const run = async () => {
    setRunning(true); setErr(""); setResult(null);
    try {
      const { data } = await api.post(`/admin/content/import-seed?overwrite=${overwrite ? "true" : "false"}`);
      setResult(data);
      onDone && await onDone();
    } catch (e) { setErr(formatApiError(e)); }
    finally { setRunning(false); }
  };
  return (
    <div data-testid="cms-import-seed" className="mb-4 rounded-2xl bg-gradient-to-r from-sky-400/15 to-emerald-400/15 border border-sky-300/30 p-5">
      <div className="flex items-start gap-4 flex-wrap">
        <div className="w-11 h-11 rounded-xl bg-sky-300/20 flex items-center justify-center shrink-0"><Upload size={20} className="text-sky-300" /></div>
        <div className="flex-1 min-w-[240px]">
          <div className="font-display font-bold text-white">One-tap content import from preview seed</div>
          <div className="text-xs text-slate-300 mt-0.5 leading-relaxed">Pulls every video URL, policy text, and copy override captured in the preview environment (bundled with this deploy) into this database. Existing overrides here are kept unless you tick "Overwrite existing" — safe to run on a fresh production DB.</div>
          <label className="mt-3 inline-flex items-center gap-2 text-xs text-slate-300 select-none">
            <input type="checkbox" data-testid="cms-import-seed-overwrite" checked={overwrite} onChange={(e) => setOverwrite(e.target.checked)} className="accent-sky-400" />
            Overwrite existing overrides in this environment
          </label>
        </div>
        <button type="button" onClick={run} disabled={running} data-testid="cms-import-seed-run" className="text-sm font-semibold text-[#0F1020] bg-gradient-to-r from-sky-300 to-emerald-300 px-4 py-2 rounded-full hover:opacity-90 disabled:opacity-50 inline-flex items-center gap-2">
          <Upload size={14} /> {running ? "Importing…" : "Run import"}
        </button>
      </div>
      {err && <div className="mt-3 rounded-lg text-xs text-rose-300 bg-rose-500/10 border border-rose-500/30 px-3 py-2">{err}</div>}
      {result && (
        <div className="mt-3 rounded-lg text-xs text-emerald-200 bg-emerald-500/10 border border-emerald-500/30 px-3 py-2" data-testid="cms-import-seed-result">
          Imported <b>{result.saved}</b> of {result.total_in_seed} entries · skipped {result.skipped_existing} existing · {result.skipped_empty} empty · {result.invalid} invalid
        </div>
      )}
    </div>
  );
}

/** A single editable field row. Supports override/reset with a live preview for video URLs. */
function EditField({ label, contentType, itemKey, field, value, onSaved, textarea = false, isVideo = false, help, placeholder }) {
  const [val, setVal] = useState(value.override ?? "");
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState("");
  const [err, setErr] = useState("");
  const hasOverride = value.override !== null && value.override !== undefined;

  const save = async () => {
    setSaving(true); setErr(""); setStatus("");
    try {
      await api.put("/admin/content", { content_type: contentType, key: itemKey, field, value: val });
      setStatus(val === "" ? "Cleared" : "Saved");
      onSaved && await onSaved();
      setTimeout(() => setStatus(""), 1600);
    } catch (e) { setErr(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const reset = async () => {
    setVal("");
    setSaving(true); setErr(""); setStatus("");
    try {
      await api.put("/admin/content", { content_type: contentType, key: itemKey, field, value: "" });
      setStatus("Reset to default");
      onSaved && await onSaved();
      setTimeout(() => setStatus(""), 1600);
    } catch (e) { setErr(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const testidBase = `cms-${contentType}-${itemKey}-${field}`;

  return (
    <div className="rounded-xl bg-[#0F1020] border border-white/10 p-4">
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <div className="text-[10px] uppercase tracking-[0.25em] text-slate-400 font-semibold">{label}</div>
          {hasOverride ? (
            <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-amber-400/20 text-amber-300" data-testid={`${testidBase}-status-override`}>Custom</span>
          ) : (
            <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-slate-500/20 text-slate-400" data-testid={`${testidBase}-status-default`}>Default</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {status && <span className="text-[11px] text-emerald-300 inline-flex items-center gap-1"><Check size={11} /> {status}</span>}
          {hasOverride && (
            <button type="button" onClick={reset} data-testid={`${testidBase}-reset`} disabled={saving} className="text-[11px] text-slate-400 hover:text-rose-300 inline-flex items-center gap-1">
              <RotateCcw size={11} /> Reset to default
            </button>
          )}
          <button type="button" onClick={save} data-testid={`${testidBase}-save`} disabled={saving} className="text-[11px] font-semibold text-[#0F1020] bg-gradient-to-r from-amber-400 to-rose-400 px-3 py-1 rounded-full hover:opacity-90 disabled:opacity-50 inline-flex items-center gap-1">
            <Save size={11} /> {saving ? "…" : "Save"}
          </button>
        </div>
      </div>
      {textarea ? (
        <textarea data-testid={`${testidBase}-input`} value={val} onChange={(e) => setVal(e.target.value)} rows={isVideo ? 3 : 6} placeholder={placeholder || (value.default || "")} className="w-full px-3 py-2 rounded-lg bg-[#151737] border border-white/10 focus:border-amber-300 focus:outline-none text-slate-100 text-sm font-mono" />
      ) : (
        <input data-testid={`${testidBase}-input`} value={val} onChange={(e) => setVal(e.target.value)} placeholder={placeholder || (value.default || "")} className="w-full px-3 py-2 rounded-lg bg-[#151737] border border-white/10 focus:border-amber-300 focus:outline-none text-slate-100 text-sm" />
      )}
      {help && <div className="mt-1.5 text-[11px] text-slate-500">{help}</div>}
      {err && <div className="mt-2 text-[11px] text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded px-2 py-1" data-testid={`${testidBase}-error`}>{err}</div>}
      {value.default && !hasOverride && !val && (
        <details className="mt-2 text-[11px] text-slate-500">
          <summary className="cursor-pointer hover:text-slate-300">Show default</summary>
          <div className="mt-1 whitespace-pre-wrap font-mono text-[10.5px] text-slate-500 bg-[#151737] border border-white/5 rounded p-2 max-h-40 overflow-auto">{value.default}</div>
        </details>
      )}
      {isVideo && (val || value.default) && (
        <details className="mt-2 text-[11px] text-slate-400">
          <summary className="cursor-pointer hover:text-slate-200 inline-flex items-center gap-1"><Eye size={11} /> Preview embed</summary>
          <div className="mt-2 max-w-xl"><VideoEmbed value={val || value.default} title={label} testid={`${testidBase}-preview`} /></div>
        </details>
      )}
    </div>
  );
}

/** Collapsible group card. */
function Group({ title, subtitle, defaultOpen, children, testid }) {
  const [open, setOpen] = useState(!!defaultOpen);
  return (
    <div className="rounded-2xl bg-[#151737] border border-white/10" data-testid={testid}>
      <button type="button" onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between px-5 py-3.5 text-left">
        <div>
          <div className="font-display text-base font-bold text-white">{title}</div>
          {subtitle && <div className="text-[11px] text-slate-400 mt-0.5">{subtitle}</div>}
        </div>
        {open ? <ChevronDown size={16} className="text-slate-400" /> : <ChevronRight size={16} className="text-slate-400" />}
      </button>
      {open && <div className="px-5 pb-5 space-y-3">{children}</div>}
    </div>
  );
}

const TABS = [
  { key: "sessions", label: "Sessions", Icon: Video },
  { key: "symptoms", label: "Symptoms", Icon: FileText },
  { key: "tribes", label: "Tribes", Icon: Users },
  { key: "education_modules", label: "Education modules", Icon: BookOpen },
  { key: "policies", label: "Policies", Icon: Shield },
];

export default function AdminContent() {
  const [tab, setTab] = useState("sessions");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/admin/content/catalog");
      setData(data);
    } catch (e) { setErr(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { setLoading(true); load(); }, []);

  return (
    <AdminShell subtitle="Content management" title="Videos & written copy">
      <ImportSeed onDone={load} />

      <Link to="/admin/content/bulk-videos" data-testid="cms-bulk-videos-link" className="mb-4 group rounded-2xl bg-gradient-to-r from-amber-400/15 to-rose-400/15 border border-amber-300/30 hover:border-amber-300 p-5 flex items-center justify-between transition-all">
        <div className="flex items-center gap-4">
          <div className="w-11 h-11 rounded-xl bg-amber-300/20 flex items-center justify-center"><ClipboardList size={20} className="text-amber-300" /></div>
          <div>
            <div className="font-display font-bold text-white">Bulk video import — Kajabi checklist</div>
            <div className="text-xs text-slate-300 mt-0.5">See every video the app needs, paste all your Kajabi embeds at once, or download a CSV to work through offline.</div>
          </div>
        </div>
        <div className="text-amber-300 group-hover:translate-x-0.5 transition-transform text-sm font-semibold">Open →</div>
      </Link>

      <Link to="/admin/content/certificate-templates" data-testid="cms-cert-templates-link" className="mb-4 group rounded-2xl bg-gradient-to-r from-rose-400/15 to-amber-400/15 border border-rose-300/30 hover:border-rose-300 p-5 flex items-center justify-between transition-all">
        <div className="flex items-center gap-4">
          <div className="w-11 h-11 rounded-xl bg-rose-300/20 flex items-center justify-center"><Award size={20} className="text-rose-300" /></div>
          <div>
            <div className="font-display font-bold text-white">Certificate templates — illustrated per-level artwork</div>
            <div className="text-xs text-slate-300 mt-0.5">Upload the 36 tribe-and-level illustrations (Thrive Tribe, Nasal Ninja, Myo Princess × Levels 1-12) and position the child's name and date once per template.</div>
          </div>
        </div>
        <div className="text-rose-300 group-hover:translate-x-0.5 transition-transform text-sm font-semibold">Open →</div>
      </Link>

      <div className="rounded-2xl bg-[#151737] border border-white/10 p-3 mb-4 flex items-center gap-1 flex-wrap">
        {TABS.map((t) => {
          const Icon = t.Icon;
          const isActive = tab === t.key;
          return (
            <button key={t.key} type="button" onClick={() => setTab(t.key)} data-testid={`cms-tab-${t.key}`} className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition ${isActive ? "bg-white/10 text-white" : "text-slate-400 hover:bg-white/5 hover:text-slate-200"}`}>
              <Icon size={14} /> {t.label}
            </button>
          );
        })}
        <div className="ml-auto text-[11px] text-slate-500">Changes apply immediately for all families.</div>
      </div>

      {err && <div className="rounded-lg text-sm text-rose-300 bg-rose-500/10 border border-rose-500/30 px-3 py-2 mb-4">{err}</div>}
      {loading && <div className="text-slate-400 text-sm">Loading catalog…</div>}
      {!loading && data && tab === "sessions" && (
        <div className="space-y-3">
          {data.sessions.map((s) => (
            <Group key={s.key} title={`Session ${s.number} · ${s.title}`} subtitle={s.focus} testid={`cms-session-${s.number}`}>
              <div className="text-[11px] uppercase tracking-[0.25em] text-amber-300 font-semibold">Start Here</div>
              <EditField label="Start-Here video (URL or iframe embed)" contentType="session_start_here" itemKey={s.key} field="video_url" value={s.start_here.video_url} onSaved={load} isVideo help="Paste a Kajabi/YouTube/Vimeo URL, or the full <iframe> embed code Kajabi provides." />
              <EditField label="Start-Here summary" contentType="session_start_here" itemKey={s.key} field="summary" value={s.start_here.summary} onSaved={load} textarea help="What parents see on the Start Here screen for this session." />
              <div className="text-[11px] uppercase tracking-[0.25em] text-amber-300 font-semibold mt-4">Practice with Chelcie</div>
              <EditField label="Practice-with-Chelcie combined video" contentType="practice_with_chelcie" itemKey={s.key} field="video_url" value={s.practice_with_chelcie.video_url} onSaved={load} isVideo help="One video per session that walks families through all exercises in sequence, timed to match the durations." />
              <div className="text-[11px] uppercase tracking-[0.25em] text-amber-300 font-semibold mt-4">Exercises ({s.exercises.length})</div>
              {s.exercises.map((e) => (
                <div key={e.key} className="rounded-xl bg-[#0F1020] border border-white/10 p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <div className="text-sm font-semibold text-white">{e.name}</div>
                      <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500">{e.category}</div>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <EditField label="Exercise video (URL or iframe embed)" contentType="session_exercise" itemKey={e.key} field="video_url" value={e.fields.video_url} onSaved={load} isVideo />
                    <EditField label="Instructions" contentType="session_exercise" itemKey={e.key} field="instructions" value={e.fields.instructions} onSaved={load} textarea />
                  </div>
                </div>
              ))}
            </Group>
          ))}
        </div>
      )}
      {!loading && data && tab === "symptoms" && (
        <div className="space-y-3">
          {data.symptoms.map((s) => (
            <Group key={s.key} title={s.name} testid={`cms-symptom-${s.key}`}>
              <EditField label="Video (URL or iframe embed)" contentType="symptom" itemKey={s.key} field="video_url" value={s.fields.video_url} onSaved={load} isVideo />
              <EditField label="Description" contentType="symptom" itemKey={s.key} field="description" value={s.fields.description} onSaved={load} textarea />
              <EditField label="Safety note (optional)" contentType="symptom" itemKey={s.key} field="safety_note" value={s.fields.safety_note} onSaved={load} textarea help="Shown as a highlighted callout on the symptom detail page." />
            </Group>
          ))}
        </div>
      )}
      {!loading && data && tab === "tribes" && (
        <div className="space-y-3">
          {data.tribes.map((t) => (
            <Group key={t.key} title={t.name} testid={`cms-tribe-${t.key}`} defaultOpen>
              <EditField label="Welcome video (URL or iframe embed)" contentType="tribe" itemKey={t.key} field="video_url" value={t.fields.video_url} onSaved={load} isVideo />
              <EditField label="Welcome text" contentType="tribe" itemKey={t.key} field="welcome_text" value={t.fields.welcome_text} onSaved={load} textarea />
              <EditField label="Manifesto" contentType="tribe" itemKey={t.key} field="manifesto" value={t.fields.manifesto} onSaved={load} textarea />
              <EditField label="Next steps (one per line)" contentType="tribe" itemKey={t.key} field="next_steps" value={t.fields.next_steps} onSaved={load} textarea />
              <EditField label="Graduation name" contentType="tribe" itemKey={t.key} field="graduation_name" value={t.fields.graduation_name} onSaved={load} />
              <EditField label="Graduation description" contentType="tribe" itemKey={t.key} field="graduation_description" value={t.fields.graduation_description} onSaved={load} textarea />
            </Group>
          ))}
        </div>
      )}
      {!loading && data && tab === "education_modules" && (
        <div className="space-y-3">
          {data.education_modules.map((m) => (
            <Group key={m.key} title={m.name} testid={`cms-module-${m.key}`} defaultOpen>
              <EditField label="Video (URL or iframe embed)" contentType="education_module" itemKey={m.key} field="video_url" value={m.fields.video_url} onSaved={load} isVideo />
              <EditField label="Title" contentType="education_module" itemKey={m.key} field="title" value={m.fields.title} onSaved={load} />
              <EditField label="Subtitle" contentType="education_module" itemKey={m.key} field="subtitle" value={m.fields.subtitle} onSaved={load} />
              <EditField label="Body" contentType="education_module" itemKey={m.key} field="body" value={m.fields.body} onSaved={load} textarea />
            </Group>
          ))}
        </div>
      )}
      {!loading && data && tab === "policies" && (
        <div className="space-y-3">
          {(data.policies || []).map((p) => (
            <Group key={p.key} title={p.name} subtitle={p.help} testid={`cms-policy-${p.key}`} defaultOpen>
              <EditField
                label="Full document text"
                contentType="policy"
                itemKey={p.key}
                field="text"
                value={p.fields.text}
                onSaved={load}
                textarea
                help="Paste the finalized attorney-reviewed copy here. Shown verbatim on the public /terms landing page, which parents open in a new tab from the enrollment Terms checkbox."
              />
            </Group>
          ))}
        </div>
      )}
    </AdminShell>
  );
}
